import './MarketPerformance.scss';
import { useEffect, useMemo } from 'react';
import { Background } from '@Components/designConstants';
import { useDmaNames } from '@Components/DmaMap/useDmaNames';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Spinner } from '@Components/Spinner';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useSetError } from '@src/redux/modals';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { MarketPerformanceTable } from './MarketPerformanceTable';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { DashboardData } from '../dtc-commerce-mmm-types';
import { useMarketData } from '../hooks/useMarketData';
import { shapeMarketTableExport } from '../utils/dtc-commerce-mmm-export';
import { summarizeMarkets } from '../utils/dtc-commerce-mmm-markets';

type MarketsSummary = ReturnType<typeof summarizeMarkets>;

interface MarketPerformanceProps {
  data: DashboardData;
  startOpen?: boolean;
}

export const MarketPerformance = ({ data, startOpen = true }: MarketPerformanceProps) => {
  const { attributionType, buildExportFilename } = useDtcCommerceMmmData();
  const setError = useSetError();
  const { data: markets, isLoading, error } = useMarketData(data);
  // Without names the table still works, labelled by DMA code, so a failure here stays quiet.
  const { data: dmaNames } = useDmaNames();

  // Only this section goes without: the rest of the page doesn't wait on market data.
  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load market data",
        message: `Failed to load market data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const isRevenue = data.modelInfo.kpiType === 'revenue';
  const summary = useMemo<MarketsSummary | null>(
    () =>
      markets
        ? summarizeMarkets(
            markets[attributionType],
            markets.sales,
            markets.accuracy,
            data.t12mStartDate,
            data.t12mEndDate
          )
        : null,
    [markets, attributionType, data.t12mStartDate, data.t12mEndDate]
  );

  const handleExcelDownload = () => {
    if (!summary) return;
    exportToExcel(
      shapeMarketTableExport(summary.markets, summary.total, isRevenue, dmaNames),
      buildExportFilename(data.modelInfo.client, 'market-performance-table')
    );
  };

  const handlePngDownload = () => {
    downloadPNG(
      '.market-performance',
      buildExportFilename(data.modelInfo.client, 'market-performance-table')
    );
  };

  return (
    <WidgetContainer
      background={Background.DARK}
      collapsible
      startOpen={startOpen}
      header='Market Performance'
      subHeader={`Actualized spend, ${isRevenue ? 'revenue' : 'conversions'}, and ${isRevenue ? 'return on ad spend' : 'cost per acquisition'} by market`}
      rightActions={
        summary && <DownloadDropdown onClickOptions={[handleExcelDownload, handlePngDownload]} />
      }
    >
      <div className='market-performance'>
        <MarketPerformanceBody
          summary={summary}
          isLoading={isLoading}
          isRevenue={isRevenue}
          dmaNames={dmaNames}
        />
      </div>
    </WidgetContainer>
  );
};

interface MarketPerformanceBodyProps {
  summary: MarketsSummary | null;
  isLoading: boolean;
  isRevenue: boolean;
  dmaNames?: Record<string, string>;
}

const MarketPerformanceBody = ({
  summary,
  isLoading,
  isRevenue,
  dmaNames
}: MarketPerformanceBodyProps) => {
  if (isLoading) return <Spinner />;
  if (!summary) {
    return <p className='market-performance-message'>Market data couldn&apos;t be loaded.</p>;
  }
  return (
    <MarketPerformanceTable
      markets={summary.markets}
      total={summary.total}
      isRevenue={isRevenue}
      dmaNames={dmaNames}
    />
  );
};
